import React, { useEffect, useCallback } from 'react';

import { FormattedMessage } from 'react-intl-next';
import { useDispatch, useSelector } from 'react-redux';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import ModalDialog, {
  ModalBody,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';
import AKSpinner from '@atlaskit/spinner';

import { CommentThread } from 'src/components/conversation/src/components/comment-tree';
import {
  apiCommentId,
  threadToConversation,
} from 'src/components/conversation-provider/utils/to-conversation';
import { FabricUserAvatar } from 'src/containers/fabric-user-avatar';
import { FabricUserName } from 'src/containers/fabric-user-name';
import { getGlobalIsColorBlindModeEnabled } from 'src/redux/diff-settings';
import {
  CLOSE_REVIEW_DRAFT_DIALOG,
  FETCH_PENDING_COMMENT_CONTEXT,
} from 'src/redux/pull-request/actions';
import {
  getCommentTree,
  getIsLoadingPendingCommentContext,
} from 'src/redux/pull-request/selectors';
import {
  getNumPendingComments,
  getPendingTaskCount,
} from 'src/redux/pull-request/selectors/review-selectors';
import { getCurrentFabricUser } from 'src/selectors/conversation-selectors';
import { getSyntaxHighlightingEnabled } from 'src/selectors/feature-selectors';
import {
  getPendingGlobalTasks,
  getPendingTasks,
} from 'src/selectors/task-selectors';

import { TaskList } from '../../components/tasks/task-list';

import messages from './comments-dialog.i18n';
import * as styles from './comments-dialog.style';
import {
  RenderConversationOptions,
  ConversationWithContext,
} from './conversation-with-context';

type ReviewDraftDialogHeaderProps = {
  /** Whether or not the review has pending comments */
  hasPendingComments: boolean;
  /** Whether or not the review has pending tasks */
  hasPendingTasks: boolean;
};

const ReviewDialogHeader = (
  props: ReviewDraftDialogHeaderProps
): JSX.Element => {
  const { hasPendingComments, hasPendingTasks } = props;

  let title = messages.reviewTitleBoth;
  if (hasPendingComments && !hasPendingTasks)
    title = messages.reviewTitleComments;
  else if (hasPendingTasks && !hasPendingComments)
    title = messages.reviewTitleTasks;

  return (
    <styles.Header>
      <ModalHeader>
        <ModalTitle>
          <FormattedMessage {...title} />
        </ModalTitle>
      </ModalHeader>
    </styles.Header>
  );
};

export const ReviewDraftDialog = (): JSX.Element => {
  const isColorBlindModeEnabled = useSelector(getGlobalIsColorBlindModeEnabled);
  const isSyntaxHighlightingEnabled = useSelector(getSyntaxHighlightingEnabled);

  const isLoading = useSelector(getIsLoadingPendingCommentContext);

  // Fabric User is used to render the avatar of the user who left a review
  const currentUser = useSelector(getCurrentFabricUser);

  const pendingGlobalTasks = useSelector(getPendingGlobalTasks);

  const hasPendingComments = useSelector(getNumPendingComments) > 0;
  const pendingTasks = useSelector(getPendingTasks);
  const hasPendingTasks = useSelector(getPendingTaskCount) > 0;
  const commentTree = useSelector(getCommentTree);

  const dispatch = useDispatch();

  // Fetch context lines for comments that need them
  useEffect(() => {
    dispatch({ type: FETCH_PENDING_COMMENT_CONTEXT.REQUEST });
  }, [dispatch]);

  const closeDialog = useCallback(() => {
    dispatch({ type: CLOSE_REVIEW_DRAFT_DIALOG });
  }, [dispatch]);

  // Prevent the dialog from closing the 'Comments in Review' popup onClose
  const handleClose = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    closeDialog();
  };

  // Close the dialog when there are no pending comments or tasks
  useEffect(() => {
    if (!hasPendingComments && !hasPendingTasks) {
      closeDialog();
    }
  }, [hasPendingComments, hasPendingTasks, closeDialog]);

  const Footer = () => (
    <styles.RightAlignedFooter>
      <CustomThemeButton appearance="primary" onClick={handleClose}>
        <FormattedMessage {...messages.reviewDialogCloseButton} />
      </CustomThemeButton>
    </styles.RightAlignedFooter>
  );

  const renderThreadComments = (
    commentThreads: CommentThread[]
  ): JSX.Element[] => {
    return commentThreads.map(thread => {
      const conversation = threadToConversation(thread);
      const conversationOptions: RenderConversationOptions = {
        isColorBlindModeEnabled,
        isSyntaxHighlightingEnabled,
        showLikeActionOnly: true,
        meta: {
          path: conversation.meta.path,
          to: conversation.meta.to || conversation.meta.from,
        },
      };
      return (
        <ConversationWithContext
          key={conversation.conversationId}
          commentThread={thread}
          options={conversationOptions}
        />
      );
    });
  };

  const renderGlobalTasks = () => {
    return pendingGlobalTasks?.length ? (
      <styles.ConversationContainer>
        <FabricUserAvatar size="medium" user={currentUser} />
        <styles.Conversation>
          <styles.SubtitleTask>
            <FormattedMessage
              {...messages.subtitleTask}
              values={{
                name: <FabricUserName user={currentUser} />,
              }}
            />
          </styles.SubtitleTask>
          <TaskList taskList={pendingGlobalTasks} renderAvatar />
        </styles.Conversation>
      </styles.ConversationContainer>
    ) : null;
  };

  const commentIdsWithPendingTasks = new Set(
    pendingTasks.flatMap(t => (t.comment ? [t.comment.id.toString()] : []))
  );
  const filteredTree = commentTree.filterMatchingSubtrees(c => {
    return (
      !!c.pending || commentIdsWithPendingTasks.has(apiCommentId(c).toString())
    );
  });
  const globalThreads = filteredTree.globalThreads();
  const fileThreads = filteredTree.fileThreads();

  return (
    <ModalDialog
      onClose={handleClose}
      autoFocus={false}
      shouldCloseOnEscapePress
      height="100%"
      width="90vw"
    >
      <ReviewDialogHeader
        hasPendingComments={hasPendingComments}
        hasPendingTasks={hasPendingTasks}
      />
      <ModalBody>
        <styles.Container>
          {isLoading && (
            <styles.LoadingContainer>
              <AKSpinner size="medium" testId="draft-loading-spinner" />
            </styles.LoadingContainer>
          )}
          {!isLoading && (
            <>
              {globalThreads.length > 0 && renderThreadComments(globalThreads)}
              {renderGlobalTasks()}
              {renderThreadComments(fileThreads)}
            </>
          )}
        </styles.Container>
      </ModalBody>
      <Footer />
    </ModalDialog>
  );
};
